// TOUR-2 — DOM target convention for assisted navigation highlights.
//
// ## Naming convention
//
// - **Mode navigation** — `mode-nav-<modeId>` where `<modeId>` is a
//   `PrimaryMode` key (`mode-nav-leads`, `mode-nav-mfgInventory`). Wired
//   dynamically in `SidebarModeList` + `MobileBottomTabBar` via
//   `fireflyModeNavTargetId()` — do not hardcode one anchor per mode file.
//
// - **Feature surfaces** — `<feature>-<action>` in kebab-case, prefixed by
//   mode or domain (`kb-search`, `ff-intake-fill`, `admin-invite-send`,
//   `sc-daily-checklist`). Attach to the interactive element with
//   `data-firefly-target` / `FIREFLY_TARGET_ATTR`.
//
// ## Integrity chain
//
// Tour step `anchorId` → `FIREFLY_TARGET_REGISTRY` → DOM wiring in
// `src/components/**` (or conditional allowlist in
// `fireflyTargetIntegrity.ts`). Locked by `tests/fireflyTargetIntegrity.test.ts`.

export const FIREFLY_TARGET_ATTR = "data-firefly-target";

export type FireflyTargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
};

function isVisibleFireflyTarget(el: HTMLElement): boolean {
  if (el.getClientRects().length > 0) return true;
  const style = window.getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden";
}

export function queryFireflyTargetById(targetId: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const trimmed = targetId.trim();
  if (!trimmed) return null;
  const nodes = document.querySelectorAll<HTMLElement>(
    `[${FIREFLY_TARGET_ATTR}="${trimmed}"]`,
  );
  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0];
  for (const el of nodes) {
    if (isVisibleFireflyTarget(el)) return el;
  }
  return nodes[0];
}

const DOCK_PANEL_SELECTOR = '[data-app-panel="ActivityDockExpandedPanel"]';

/**
 * Bring a tour target into view. Targets inside the activity panel scroll
 * that panel (instant) so the ring is not left on the block above them.
 * Everything else uses the browser's nearest-block scroll.
 */
export function scrollFireflyTargetIntoView(element: HTMLElement): void {
  const dock = element.closest(DOCK_PANEL_SELECTOR);
  if (dock instanceof HTMLElement) {
    const dockRect = dock.getBoundingClientRect();
    const elRect = element.getBoundingClientRect();
    dock.scrollTop += elRect.top - dockRect.top - 72;
    return;
  }
  element.scrollIntoView({ block: "nearest", behavior: "auto" });
}

export function readFireflyTargetRect(element: HTMLElement): FireflyTargetRect {
  const box = element.getBoundingClientRect();
  return {
    top: box.top,
    left: box.left,
    width: box.width,
    height: box.height,
    centerX: box.left + box.width / 2,
    centerY: box.top + box.height / 2,
  };
}

/** Stable sidebar / mobile tab anchor for a primary mode (`mode-nav-leads`). */
export function fireflyModeNavTargetId(mode: string): string {
  return `mode-nav-${mode.trim()}`;
}
